import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon } from 'components';
import p4pMark from 'assets/branding/logo-icon-grey-20.png';
import { RATE_IT_COLORS, TIRI_HEADER } from '../constants';

const Bar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: ${TIRI_HEADER.TOP_OFFSET}px ${TIRI_HEADER.GUTTER}px 0;
  /* Load-bearing: the header spans the full width, and without this the whole band would
     swallow the media click that pauses a video. Children re-enable pointer events. */
  pointer-events: none;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const CircleButton = styled.button`
  width: ${TIRI_HEADER.BUTTON_SIZE}px;
  height: ${TIRI_HEADER.BUTTON_SIZE}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${TIRI_HEADER.BUTTON_BG};
  cursor: pointer;
  pointer-events: auto;

  &:focus-visible {
    outline: 2px solid ${RATE_IT_COLORS.TILE_SELECTED_BORDER};
    outline-offset: 2px;
  }
`;

/**
 * The sponsor slot: the TIRI wordmark and the p4p mark presented as ONE pill, so the two read as
 * a single lockup rather than two elements that happen to be adjacent.
 *
 * Height is pinned rather than left to the wordmark's line box. A text node is taller than the
 * glyphs it draws — the font reserves ascender and descender room that "TIRI" (all caps, no
 * descenders) never uses — so an auto-height pill sits visibly taller than its contents.
 */
const SponsorPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${TIRI_HEADER.SPONSOR_GAP}px;
  height: ${TIRI_HEADER.SPONSOR_HEIGHT}px;
  padding: 0 10px;
  border-radius: 10px;
  background: ${TIRI_HEADER.BUTTON_BG};
  border: 0.5px solid ${TIRI_HEADER.SPONSOR_BORDER};
  pointer-events: auto;
`;

const SponsorWord = styled.span`
  font-family: CeraPro, sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #fff;
  line-height: 1;
`;

const SponsorLogo = styled.img`
  height: ${TIRI_HEADER.SPONSOR_LOGO_HEIGHT}px;
  /* Sized by HEIGHT with width from the asset's own ratio — a square box would squash it. The
     mark is already near-white with its own alpha, so it needs no tint on this dark pill. */
  width: auto;
  display: block;
`;

/**
 * The header the feed wears: the way out and the TIRI lockup on one row.
 *
 * A PORT of mobile's `TiriFeedHeader` so the two surfaces can be diffed. The creator's
 * attribution used to be a second row here; like mobile, it now sits at the bottom of the card
 * directly above the tiles — see `RateItCreatorRow` and `RateItFeedBottomStack`. The screen also
 * renders this header for its loading/error/empty states, where no card exists and the visitor
 * would otherwise be stranded.
 *
 * THERE IS NO MUTE CONTROL. It used to occupy the top-right corner, where the design puts the
 * TIRI lockup. Sound is still handled — the media starts muted because browsers block unmuted
 * autoplay, and the first rating is the gesture that turns it on — but the fan can no longer
 * mute it again from here. If that has to come back it needs a corner of its own; do not put it
 * back over the lockup.
 */
const RateItHeader = ({ onBack }) => {
  const { t } = useTranslation();

  return (
    <Bar>
      <TopRow>
        <CircleButton type="button" onClick={onBack} aria-label={t('tiri.rateIt.close', 'Close')}>
          <Icon type="close" dimensions={TIRI_HEADER.BACK_ICON} col="#fff" />
        </CircleButton>

        <SponsorPill>
          {/* A brand name — translated only in the sense that it has a key at all. */}
          <SponsorWord>{t('tiri.sponsorPlaceHolder', 'TIRI')}</SponsorWord>
          {/* The wordmark beside it already names this, so the image is decorative. */}
          <SponsorLogo src={p4pMark} alt="" />
        </SponsorPill>
      </TopRow>
    </Bar>
  );
};

export default RateItHeader;
